import React, { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
// These two travel with the JSX that uses them: the insufficient-evidence
// warning on a competency, and the quote mark on a verbatim evidence line.
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// The interview itself: summary, competency scores with their evidence,
// confidence signals, every answer with its recording and rating, and the
// integrity record.
//
// ─── WHY THIS IS A COMPONENT AND NOT PART OF A PAGE ─────────────────────────
//
// It was the middle of ReviewConsole. Two screens now show an interview — the
// review console at /ats/review/:id, and the candidate page that replaced the
// drawer — and an interview rendered two different ways is two places for the
// evidence behind a hiring decision to drift apart. One implementation, two
// hosts.
//
// It renders NOTHING but the interview. The identity header, the hire/reject
// decision and the audit trail belong to whichever page is hosting it, because
// those are about the candidate rather than about this one interview.

// Traffic-light for a 1-5 answer rating.
//
// One function rather than the thresholds written out at each site: they are
// used by the chip on every answer AND by the overview strip above them, and
// two copies drifting apart would colour the same score differently in two
// places on one screen. The number is always rendered next to the colour — the
// colour is a scanning aid, never the information.
function ratingTone(score) {
  if (score >= 4) return { fg: "#067647", bg: "#ecfdf3", bd: "#abefc6" };
  if (score === 3) return { fg: "#b54708", bg: "#fffaeb", bd: "#fedf89" };
  return { fg: "#b42318", bg: "#fef3f2", bd: "#fecdca" };
}

/**
 * A recording, as a small clickable still.
 *
 * ─── WHY A THUMBNAIL AND NOT THE PLAYER ────────────────────────────────────
 *
 * Every answer used to render a 460px-wide <video controls>. Sixteen of those
 * is sixteen screens of mostly-black rectangle, and the transcript — the part
 * that is actually READ, and searchable, and skimmable — was pushed below each
 * one. So the video became a thumbnail beside the words rather than a player
 * above them, and clicking it opens the recording at a size worth watching.
 *
 * `#t=0.6` is the trick that makes this work: without a media fragment the
 * browser paints the first frame, which is the candidate mid-blink before they
 * have started talking, and often simply black. Seeking six tenths of a second
 * in gets a real frame of a real face.
 */
export function VideoThumb({ src, onClick, width = 176, badge, round = false, sx }) {
  const height = round ? width : Math.round(width * 0.62);
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        width,
        height,
        flex: "none",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        borderRadius: round ? "50%" : "10px",
        bgcolor: "#0c0a09",
        border: "1px solid #e7e5e4",
        "&:hover .iv-play": { transform: "scale(1.12)" },
        "&:focus-visible": { outline: "2px solid #0f766e", outlineOffset: 2 },
        ...sx,
      }}
    >
      <video
        src={`${src}#t=0.6`}
        preload="metadata"
        muted
        playsInline
        tabIndex={-1}
        style={{
          width: "100%",
          height: "100%",
          // Faces sit in the upper half of a webcam frame, so a centred crop
          // of a portrait cuts the chin off. Bias the crop upward.
          objectFit: "cover",
          objectPosition: "center 28%",
          display: "block",
          pointerEvents: "none",
        }}
      />
      <Box
        className="iv-play"
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          transition: "transform .15s ease",
        }}
      >
        <Box
          sx={{
            width: round ? 26 : 34,
            height: round ? 26 : 34,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,.92)",
            display: "grid",
            placeItems: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,.35)",
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: round ? 17 : 22, color: "#0f172a" }} />
        </Box>
      </Box>
      {badge && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            px: 0.75,
            py: 0.35,
            bgcolor: "rgba(0,0,0,.55)",
          }}
        >
          <Typography sx={{ fontSize: 10.5, color: "#fff", fontWeight: 700 }}>{badge}</Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * A competency score as a dial.
 *
 * A 270° arc rather than a full ring: the gap at the bottom is what makes the
 * ends readable as a scale with a start and an end, and it leaves room for the
 * number without crowding it.
 *
 * @param {number} score 0-5, as the score breakdown reports it
 */
function Gauge({ score, size = 88, stroke = 9 }) {
  const pct = Math.max(0, Math.min(1, Number(score) / 5));
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  // Three quarters of the ring is the track; the arc fills that fraction of it.
  const track = circumference * 0.75;
  // Same thresholds as every other score on this screen — one source, so a
  // 4/5 cannot be green on a chip and amber on a dial.
  const tone = ratingTone(Math.round(Number(score)));

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Rotated so the 270° opening sits at the bottom. */}
        <g transform={`rotate(135 ${size / 2} ${size / 2})`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={tone.bd}
            strokeWidth={stroke}
            strokeDasharray={`${track} ${circumference}`}
            strokeLinecap="round"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={tone.fg}
            strokeWidth={stroke}
            strokeDasharray={`${track * pct} ${circumference}`}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray .5s ease" }}
          />
        </g>
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
        <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
          {Math.round(pct * 100)}
          <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8" }}>
            %
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
}

const SIGNAL_LABEL = {
  hedging: "Hedging",
  specificity: "Specificity",
  self_correction: "Self-correction",
  response_latency: "Response latency",
  filler_ratio: "Filler words",
};

/**
 * @param {object} props
 * @param {object} props.data  the /review/session/:id payload, verbatim
 */
export default function InterviewReview({ data }) {
  // Which recording is open, or null. Declared BEFORE the early return below —
  // a hook after a conditional return is a hook that sometimes does not run,
  // which React will not forgive.
  const [playing, setPlaying] = useState(null);

  if (!data) return null;
  // Derived server-side from what was actually asked and answered, so it covers
  // interviews sat long before anything was measuring them. Absent on an older
  // API response, which simply renders nothing.
  const { session, rubric, items = [], health = null } = data;
  const breakdown = session?.score_breakdown?.breakdown || [];
  const signals = session?.confidence_breakdown?.signals || [];
  const weightByKey = new Map((rubric?.competencies || []).map((c) => [c.key, c]));

  // The per-answer ratings, for the overview strip.
  //
  // The average is over the RATED answers only. Counting an unrated warm-up as
  // a zero would drag the figure down for something the candidate was never
  // being marked on, and counting it as anything else would be an invention.
  const ratedItems = items.filter((i) => i.ai_score != null);
  const avgRating = ratedItems.length
    ? (ratedItems.reduce((sum, i) => sum + Number(i.ai_score), 0) / ratedItems.length).toFixed(1)
    : null;

  return (
    <>
    {/* ── What this verdict rests on ─────────────────────────────────────
        ABOVE the summary, deliberately, and this is the whole point of it.

        A six-question interview and a sixteen-question one used to look
        identical here: same layout, same confident paragraph, same score out
        of five. So did one where half the recordings were silence. A reviewer
        reading the summary first has already formed a view by the time they
        notice the answers are thin — if they notice at all.

        It sits BESIDE the score rather than adjusting it. The number is what
        the rubric arithmetic produced and must stay defensible; what changes
        is whether a human should act on it. */}
    {health && !health.ok && (
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mt: 2, borderRadius: 3,
          border: `1px solid ${health.thin ? "#fed7aa" : "#e2e8f0"}`,
          bgcolor: health.thin ? "#fffbeb" : "#fafaf9",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
          <WarningAmberIcon
            sx={{ fontSize: 19, color: health.thin ? "#b45309" : "#a8a29e" }}
          />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1c1917" }}>
            {health.thin
              ? `This verdict rests on ${health.answered} of ${health.scoreable} questions`
              : "Worth knowing about this interview"}
          </Typography>
        </Stack>

        <Stack spacing={1.25}>
          {health.issues.map((issue) => (
            <Box key={issue.code}>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: issue.severity === "warning" ? "#b45309" : "#57534e",
                }}
              >
                {issue.label}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "#78716c", lineHeight: 1.55 }}>
                {issue.detail}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    )}

    {/* ── Summary ────────────────────────────────────────────────────── */}
    {session.score_breakdown?.summary && (
      <Paper elevation={0} sx={{ p: 3, mt: 2, borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Typography sx={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 11, fontWeight: 500, letterSpacing: ".1em", textTransform: "uppercase", color: "#a8a29e", mb: 1.5 }}>Summary</Typography>
        <Typography sx={{ fontSize: 15, lineHeight: 1.6 }}>
          {session.score_breakdown.summary}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 2 }}>
          {["strengths", "concerns"].map((key) => {
            const list = session.score_breakdown?.[key] || [];
            if (!list.length) return null;
            return (
              <Box key={key} sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                  {key}
                </Typography>
                {list.map((x, i) => (
                  <Typography key={i} sx={{ fontSize: 14, mt: 0.5 }}>• {x}</Typography>
                ))}
              </Box>
            );
          })}
        </Stack>
      </Paper>
    )}

    {/* ── Competency breakdown ───────────────────────────────────────── */}
    {breakdown.length > 0 && (
      <Paper elevation={0} sx={{ p: 3, mt: 2, borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Typography sx={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 11, fontWeight: 500, letterSpacing: ".1em", textTransform: "uppercase", color: "#a8a29e", mb: 2.5 }}>Skill performance breakdown</Typography>

        {/* ── Every competency as a dial ────────────────────────────────────
            The list below carries the weight, the verdict and the verbatim
            evidence — everything needed to DEFEND a score. This grid is for
            the question that comes first: where is this person strong and
            where are they weak? Eight stacked progress bars do not answer
            that at a glance; eight dials do.
            One card per competency defined on the rubric. There is no
            hard-skill / soft-skill split here because the rubric does not
            record one — a competency is { key, label, weight, anchors }. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
            gap: 1.25,
            mb: 3,
          }}
        >
          {breakdown.map((row) => {
            const comp = weightByKey.get(row.competency);
            const label = comp?.label || row.competency;
            return (
              <Tooltip
                key={`g-${row.competency}`}
                title={
                  row.insufficient_evidence
                    ? `${label} — the interview did not establish enough to judge this. Excluded from the score.`
                    : `${label} — ${row.score}/5, weight ${comp?.weight ?? "?"}%`
                }
              >
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: "10px",
                    border: "1px solid #e7e5e4",
                    bgcolor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: "#44403c",
                      textAlign: "center",
                      lineHeight: 1.35,
                      // Long competency names are the norm ("preparing BRD,
                      // FRD, SRS, User stories"), so the label wraps and every
                      // card keeps the same height rather than the row going
                      // ragged.
                      minHeight: 46,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {label}
                  </Typography>

                  {row.insufficient_evidence ? (
                    <Box
                      sx={{
                        width: 88,
                        height: 88,
                        borderRadius: "50%",
                        border: "9px solid #f5f5f4",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <WarningAmberIcon sx={{ fontSize: 22, color: "#b45309" }} />
                    </Box>
                  ) : (
                    <Gauge score={row.score} />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Stack spacing={2.5}>
          {breakdown.map((row) => {
            const comp = weightByKey.get(row.competency);
            return (
              <Box key={row.competency}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 600 }}>
                    {comp?.label || row.competency}
                    <Typography component="span" sx={{ color: "text.secondary", fontSize: 13 }}>
                      {" "}· {comp?.weight ?? "?"}%
                    </Typography>
                  </Typography>
                  {row.insufficient_evidence ? (
                    <Tooltip title="The interview did not establish enough to judge this. Excluded from the score rather than counted against the candidate.">
                      <Chip
                        size="small"
                        icon={<WarningAmberIcon />}
                        label="No evidence"
                        sx={{ bgcolor: "#fffbeb", color: "#b45309", fontWeight: 700 }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      size="small"
                      label={`${row.score} / 5`}
                      sx={{ bgcolor: "#effcf9", color: "#0f766e", fontWeight: 700 }}
                    />
                  )}
                </Stack>

                {!row.insufficient_evidence && (
                  <LinearProgress
                    variant="determinate"
                    value={(row.score / 5) * 100}
                    sx={{
                      mt: 1, height: 6, borderRadius: 3, bgcolor: "#e0dedb",
                      "& .MuiLinearProgress-bar": { bgcolor: "#0f766e" },
                    }}
                  />
                )}

                {row.evidence_quote && (
                  // The quote is the whole point. A score without the words it
                  // came from is a number the reviewer has to take on faith.
                  <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: "3px solid #cbd5e1" }}>
                    <Stack direction="row" spacing={0.5}>
                      <FormatQuoteIcon sx={{ fontSize: 16, color: "#a8a29e" }} />
                      <Typography sx={{ fontSize: 14, fontStyle: "italic", color: "#44403c" }}>
                        {row.evidence_quote}
                      </Typography>
                    </Stack>
                  </Box>
                )}
                {row.rationale && (
                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.75 }}>
                    {row.rationale}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Paper>
    )}

    {/* ── Confidence signals ─────────────────────────────────────────── */}
    {signals.length > 0 && (
      <Paper elevation={0} sx={{ p: 3, mt: 2, borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Typography sx={{ fontWeight: 700 }}>Confidence signals</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
          Scored from language and timing only — never from the candidate's face.
        </Typography>
        <Stack spacing={1.5}>
          {signals.map((s) => (
            // Label and score on one line, the evidence beneath, once the
            // three-column version would leave the evidence a word wide.
            <Stack
              key={s.signal}
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.75, sm: 2 }}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                <Typography
                  sx={{ width: { xs: "auto", sm: 150 }, fontSize: 14, fontWeight: 600 }}
                >
                  {SIGNAL_LABEL[s.signal] || s.signal}
                </Typography>
                <Chip
                  size="small"
                  label={`${s.score}/5`}
                  sx={{ bgcolor: "#f5f5f4", fontWeight: 700 }}
                />
              </Stack>
              <Typography sx={{ fontSize: 13, color: "text.secondary", flex: 1 }}>
                {s.evidence}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>
    )}

    {/* ── Answers ────────────────────────────────────────────────────── */}
    <Paper elevation={0} sx={{ p: 3, mt: 2, borderRadius: 3, border: "1px solid #e2e8f0" }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="baseline"
        sx={{ mb: 2, flexWrap: "wrap", rowGap: 0.5 }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          Answers ({items.filter((i) => i.answered).length} of {items.length})
        </Typography>
        {/* Said once, at the top. Repeating "rated by AI" on sixteen chips
            would be noise; saying it nowhere would leave a number next to a
            person's face with no indication of who put it there. */}
        {items.some((i) => i.ai_score != null) && (
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            Each answer is rated 1–5 by the AI on how well it answered that
            question — separately from the competency scores above.
          </Typography>
        )}
      </Stack>

      {/* ── Every rating, in one glance ──────────────────────────────────────
          The rating for each answer is already on that answer, but reading
          sixteen of them means sixteen scrolls past transcripts and videos —
          and "which questions did they actually do badly on" is a question
          about the SHAPE of the interview, not about any one answer.
          Every question appears, not only the rated ones: a gap in the row is
          itself worth seeing. Clicking a tile jumps to that answer. */}
      {ratedItems.length > 0 && (
        <Paper
          elevation={0}
          sx={{ p: 1.75, mb: 2.5, borderRadius: "10px", border: "1px solid #e7e5e4", bgcolor: "#fafaf9" }}
        >
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{ mb: 1.25, gap: 1, flexWrap: "wrap" }}
          >
            <Typography
              sx={{
                fontSize: 11, fontWeight: 800, letterSpacing: ".06em",
                textTransform: "uppercase", color: "text.secondary",
              }}
            >
              Rating by question
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
              average <strong>{avgRating}</strong> / 5 across {ratedItems.length} rated{" "}
              {ratedItems.length === 1 ? "answer" : "answers"}
            </Typography>
          </Stack>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {items.map((item) => {
              const rated = item.ai_score != null;
              const tone = rated ? ratingTone(item.ai_score) : null;
              return (
                <Tooltip
                  key={item.index}
                  title={
                    rated
                      ? `Q${item.index}: ${item.question}${item.ai_note ? ` — ${item.ai_note}` : ""}`
                      : item.ai_not_scored
                        ? `Q${item.index}: not rated — a warm-up, or no answer was given.`
                        : `Q${item.index}: ${item.question}`
                  }
                >
                  <Box
                    onClick={() => {
                      const el = document.getElementById(`ans-${item.index}`);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    sx={{
                      cursor: "pointer",
                      minWidth: 46,
                      textAlign: "center",
                      px: 0.9,
                      py: 0.55,
                      borderRadius: "8px",
                      bgcolor: rated ? tone.bg : "#f5f5f4",
                      border: `1px solid ${rated ? tone.bd : "#e7e5e4"}`,
                      transition: "transform .12s ease",
                      "&:hover": { transform: "translateY(-1px)" },
                    }}
                  >
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", lineHeight: 1.3 }}>
                      Q{item.index}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 800,
                        lineHeight: 1.3,
                        color: rated ? tone.fg : "#a8a29e",
                      }}
                    >
                      {rated ? item.ai_score : "–"}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Paper>
      )}

      <Stack spacing={3}>
        {items.map((item) => (
          <Box key={item.index} id={`ans-${item.index}`} sx={{ scrollMarginTop: "12px" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip size="small" label={`Q${item.index}`} sx={{ bgcolor: "#0f766e", color: "#fff", fontWeight: 700 }} />
              <Chip size="small" label={item.category} sx={{ bgcolor: "#f5f5f4" }} />
              {item.latency_ms != null && (
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  paused {(item.latency_ms / 1000).toFixed(1)}s · spoke{" "}
                  {Math.round((item.duration_ms || 0) / 1000)}s
                </Typography>
              )}

              <Box sx={{ flex: 1 }} />

              {/* ── How THIS answer was rated ────────────────────────────
                  Four different states, and collapsing any two of them would
                  put a number in front of a reviewer that means something
                  other than it appears to:

                    a score        the model rated this answer
                    "not scored"   a warm-up, or nothing was said. Deliberate,
                                   not missing.
                    nothing        the interview has not been scored yet — the
                                   page already says so at the top.
                    "re-score"     scored before per-answer ratings existed.
                                   A blank here would read as "the model had
                                   no opinion", which is not what happened. */}
              {item.ai_score != null ? (
                <Tooltip title={item.ai_note || "Rated by the AI on how well this answer addressed the question"}>
                  <Chip
                    size="small"
                    label={`${item.ai_score}/5`}
                    sx={{
                      fontWeight: 800,
                      color: ratingTone(item.ai_score).fg,
                      bgcolor: ratingTone(item.ai_score).bg,
                      border: `1px solid ${ratingTone(item.ai_score).bd}`,
                    }}
                  />
                </Tooltip>
              ) : item.ai_not_scored ? (
                <Tooltip
                  title={
                    item.ai_note ||
                    "Not rated — a warm-up, or no answer was given. The candidate is not penalised."
                  }
                >
                  <Chip
                    size="small"
                    label="not scored"
                    sx={{ fontSize: 11, bgcolor: "#f5f5f4", color: "text.secondary" }}
                  />
                </Tooltip>
              ) : session.status === "SCORED" && !item.rated ? (
                <Tooltip title="This interview was scored before answers were rated individually. Re-score it to get a rating per answer.">
                  <Chip
                    size="small"
                    label="no rating"
                    sx={{
                      fontSize: 11,
                      bgcolor: "#fffaeb",
                      color: "#b54708",
                      border: "1px solid #fedf89",
                    }}
                  />
                </Tooltip>
              ) : null}
            </Stack>
            <Typography sx={{ fontWeight: 600 }}>{item.question}</Typography>
            {item.why_this_question && (
              // Shown to the reviewer, never to the candidate — knowing what a
              // question probes for is exactly what would let someone game it.
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontStyle: "italic", mt: 0.25 }}>
                Probing for: {item.why_this_question}
              </Typography>
            )}

            {/* ── The answer: words on the left, recording on the right ─────
                The transcript is what gets read; the recording is what gets
                spot-checked. So the words take the column and the video takes
                a thumbnail beside them. Stacks on a narrow screen with the
                thumbnail first, because at that width a 176px still and a
                paragraph side by side leaves room for neither. */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 1,
                alignItems: "flex-start",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, order: { xs: 2, sm: 1 } }}>
                {/* ── Four outcomes, and they are not interchangeable ───────
                    a transcript      they spoke and we have the words
                    skipped           they chose to pass — a decision of theirs
                    no_speech         we measured the audio and there was none
                    transcript_error  transcription broke — our problem, not theirs
                    nothing at all    no answer row: the upload never landed

                    These used to collapse into "could not be transcribed",
                    which read as a system failure and quietly invited the
                    reviewer to discount it. A deliberate pass is evidence
                    about the candidate; a lost file is evidence about us. */}
                {item.transcript_suspect ? (
                  /* Shown ABOVE any text, because a reviewer who reads a
                     plausible-sounding answer first has already formed a view.
                     The text is still shown when there is one — seeing what was
                     returned is how somebody understands what went wrong. */
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    <strong>This transcript could not have come from the recording.</strong>{" "}
                    {item.transcript_suspect} Watch the recording — do not treat the
                    text below as something the candidate said.
                    {item.transcript ? (
                      <Typography
                        sx={{
                          mt: 1, fontSize: 13.5, fontStyle: "italic",
                          color: "#78716c", whiteSpace: "pre-wrap",
                        }}
                      >
                        {item.transcript}
                      </Typography>
                    ) : null}
                  </Alert>
                ) : item.transcript ? (
                  <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap", color: "#44403c" }}>
                    {item.transcript}
                  </Typography>
                ) : item.skipped ? (
                  <Typography sx={{ fontSize: 13.5, color: "#b45309", fontStyle: "italic" }}>
                    The candidate chose to skip this question.
                  </Typography>
                ) : item.no_speech ? (
                  <Typography sx={{ fontSize: 13.5, color: "text.secondary", fontStyle: "italic" }}>
                    No speech in this recording — nothing was said.
                    {item.audio_level
                      ? ` (peak ${item.audio_level.peak_dbfs} dBFS across ${(
                          (item.audio_level.duration_ms || 0) / 1000
                        ).toFixed(1)}s)`
                      : ""}{" "}
                    Not sent for transcription, and not scored.
                  </Typography>
                ) : item.transcript_error ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    This answer could not be transcribed ({item.transcript_error}). Please
                    watch the recording — the candidate is not penalised for this.
                  </Alert>
                ) : item.answered ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    This answer could not be transcribed. Please watch the recording —
                    the candidate is not penalised for this.
                  </Alert>
                ) : (
                  <Typography sx={{ fontSize: 13.5, color: "text.secondary", fontStyle: "italic" }}>
                    Not answered.
                  </Typography>
                )}
              </Box>

              {item.video_url ? (
                <VideoThumb
                  src={item.video_url}
                  onClick={() => setPlaying(item)}
                  badge={
                    item.duration_ms
                      ? `${Math.round(item.duration_ms / 1000)}s · play`
                      : "play"
                  }
                  sx={{ order: { xs: 1, sm: 2 } }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: 12.5,
                    color: "text.secondary",
                    flex: "none",
                    width: { xs: "auto", sm: 176 },
                    order: { xs: 1, sm: 2 },
                  }}
                >
                  No recording for this question.
                </Typography>
              )}
            </Box>
            {/* Why that rating. Under the transcript, because it is a
                comment ON the answer and reads as one only after it. */}
            {item.ai_note && item.ai_score != null && (
              <Typography
                sx={{
                  fontSize: 12.5,
                  mt: 1,
                  pl: 1.25,
                  borderLeft: "2px solid #e2e8f0",
                  color: "text.secondary",
                }}
              >
                {item.ai_note}
              </Typography>
            )}

            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}
      </Stack>
    </Paper>

    {/* ── Integrity + device ─────────────────────────────────────────── */}
    <Paper elevation={0} sx={{ p: 3, mt: 2, borderRadius: 3, border: "1px solid #e2e8f0" }}>
      <Typography sx={{ fontWeight: 700 }}>Session notes</Typography>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
        Context for you to interpret. None of this affects the score.
      </Typography>
      {session.device_check ? (
        <Typography sx={{ fontSize: 13.5 }}>
          Device check: camera {session.device_check.camera_ok ? "ok" : "not confirmed"},
          microphone {session.device_check.mic_ok ? "ok" : "not confirmed"}
          {session.device_check.mic_peak_level ? ` (peak ${session.device_check.mic_peak_level})` : ""}
          {session.device_check.connection ? ` · ${session.device_check.connection}` : ""}
        </Typography>
      ) : (
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
          No device check recorded.
        </Typography>
      )}
      <Typography sx={{ fontSize: 13.5, mt: 1 }}>
        {session.integrity_events?.length
          ? `${session.integrity_events.length} focus event(s) during the interview — the candidate switched away from the tab or window. This is common and has innocent explanations.`
          : "No focus events recorded."}
      </Typography>
      <Typography sx={{ fontSize: 13.5, mt: 1, color: "text.secondary" }}>
        Attempt {session.attempts_used} of {session.max_attempts}
      </Typography>
    </Paper>

    {/* ── The recording, when one is asked for ─────────────────────────────
        One dialog for all sixteen answers rather than a player per answer.
        The question is repeated under the video: by the time somebody has
        clicked a thumbnail and watched forty seconds of it, "which one was
        this?" is a real question. */}
    <Dialog
      open={Boolean(playing)}
      onClose={() => setPlaying(null)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      <DialogContent sx={{ p: 0, bgcolor: "#0c0a09", position: "relative", lineHeight: 0 }}>
        <IconButton
          onClick={() => setPlaying(null)}
          aria-label="Close"
          sx={{
            position: "absolute", top: 8, right: 8, zIndex: 2, color: "#fff",
            bgcolor: "rgba(0,0,0,.45)", "&:hover": { bgcolor: "rgba(0,0,0,.65)" },
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        {playing?.video_url && (
          <video
            // Keyed on the url so switching answers reloads the element rather
            // than leaving the previous recording playing behind the new one.
            key={playing.video_url}
            src={playing.video_url}
            controls
            autoPlay
            // ── Why these three ────────────────────────────────────────────
            //
            // preload: without it the attribute defaults to "auto", and these
            // recordings are 20MB+ for a minute of talking. Chrome then pulls
            // the whole file before it will report a duration or paint a
            // frame — the thumbnail beside it renders instantly because it
            // asks for "metadata" and nothing more. That difference is the
            // whole bug: same URL, same blob, one loads and one hangs.
            //
            // muted: an unmuted autoplay is refused unless Chrome sees recent
            // user activation. When it is refused the element sits paused at
            // 0:00 on a black frame, which is indistinguishable from loading.
            // Starting muted always succeeds; the reviewer needs the audio, so
            // it is unmuted again the moment playback is actually running.
            //
            // playsInline: iOS Safari otherwise takes the video fullscreen on
            // play, which throws the reviewer out of the dialog they opened.
            preload="metadata"
            muted
            playsInline
            onPlaying={(e) => {
              // Unmute once playing. Doing it on loadedmetadata instead can
              // land before play() resolves and gets the autoplay refused all
              // over again.
              if (e.currentTarget.muted) e.currentTarget.muted = false;
            }}
            style={{ width: "100%", maxHeight: "76vh", display: "block", background: "#0c0a09" }}
          />
        )}
      </DialogContent>
      {playing?.question && (
        <Box sx={{ px: 2.25, py: 1.75 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip
              size="small"
              label={`Q${playing.index}`}
              sx={{ bgcolor: "#0f766e", color: "#fff", fontWeight: 700 }}
            />
            {playing.ai_score != null && (
              <Chip
                size="small"
                label={`${playing.ai_score}/5`}
                sx={{
                  fontWeight: 800,
                  color: ratingTone(playing.ai_score).fg,
                  bgcolor: ratingTone(playing.ai_score).bg,
                  border: `1px solid ${ratingTone(playing.ai_score).bd}`,
                }}
              />
            )}
          </Stack>
          <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{playing.question}</Typography>
        </Box>
      )}
    </Dialog>

    </>
  );
}
